import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * useApi — fires an API call on mount (and when deps change) 
 * and returns { data, loading, error, refetch }
 *
 * The apiFn is stored in a ref so it never causes infinite loops,
 * even when passed as an inline arrow function.
 */
const useApi = (apiFn, deps = []) => {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  // Store apiFn in a ref — updates silently without triggering effects
  const apiFnRef = useRef(apiFn);
  useEffect(() => { apiFnRef.current = apiFn; }); // update every render

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFnRef.current();
      setData(res.data?.data ?? res.data);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetch(); }, [fetch]);

  return { data, loading, error, refetch: fetch };
};

export default useApi;